import type { DataProvider } from 'ra-core'
import { createDataProvider, DEFAULT_RESOURCE_CONFIG } from '@internal/ui-kit/providers/dataProvider'
import type { ResourceConfig } from '@internal/ui-kit/providers/dataProvider'

// TODO(open item, migration guide §10): fill in per-resource overrides here
// as each resource's real field names are confirmed (e.g. sections: { idField: 'section_id' }).
const RESOURCE_CONFIG: Partial<Record<string, ResourceConfig>> = {
  // branches' real primary key is `branch_id` (migration guide §6.2, apps/branches).
  // Confirmed against the live API (PR #15 review): page/limit pagination
  // (page starts at 1), not a cursor, and PATCH requires `branch_id` in the body.
  branches: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'branch_id',
    cursorField: 'branch_id',
    paginationStyle: 'page',
    includeIdInPatchBody: true,
    // Confirmed in review: the `title` filter is real free-text search, sent
    // to the API as `?query=`, not `?title=`. `q` (HATCH-227 review: "Parameter
    // for branches list search HAS TO BE 'query'. Where did you get 'q='
    // convention?") is `AutocompleteInput`/`AutocompleteArrayInput`'s own
    // default search-as-you-type filter key (`autocomplete-input.tsx`'s
    // `DefaultFilterToQuery`) — sent whenever `branches` is used as a
    // `ReferenceInput`/`ReferenceArrayInput` target (e.g. `apps/products`'
    // `branch_id` filter, `apps/orders`' `branch_ids`), same real `?query=`
    // param as the plain `title` filter above, not a separate convention.
    filterParamMap: { title: 'query', q: 'query' },
    // HATCH-161: `GET /branches` accepts `?include_hidden=` — no
    // `filterParamMap` entry needed since `include_hidden` (`schema.ts`'s
    // filter-only field, `apps/branches`' "Include hidden" checkbox) already
    // matches the real query param name.
    //
    // `GET /branches/{branch_id}` (this `getOnePath`) separately accepts
    // `?show_hidden=`, per the ticket's own wording — always sent here rather
    // than threaded through from the list's checkbox state: `BranchDetailPage`
    // is reached with a specific `branch_id` already in hand (row action /
    // direct URL), so there's no "should this row be visible" question left
    // to answer the way there is for a browsable list — the admin already
    // asked for this exact record.
    getOnePath: (id) => `/branches/${id}?show_hidden=true`,
    // HATCH-161 follow-up (Alex Gridnev): the real API's batch hide/unhide
    // endpoint is `PATCH /branches/hidden { branch_ids, hidden }`, not one
    // PATCH per selected branch — `updateMany`'s default per-id fallback
    // would 404 against it. `BranchesBatchActionsToolbar.tsx` calls
    // `dataProvider.updateMany('branches', { ids, data: { hidden } })`
    // rather than a one-off `httpClient` call, so this batch action goes
    // through the same data-provider layer every other mutation in this app
    // already does.
    updateManyPath: 'branches/hidden',
    updateManyIdsField: 'branch_ids',
  },
  // brand_id/insurance_provider_ids (apps/branches) reference these by their
  // own primary key field, matching the source export's
  // `getBrands`/`getInsuranceProviders` id/label mapping (retool-exports/
  // branches/queries/getBrands.json, getInsuranceProviders.json) — both are
  // plain `GET /brands` / `GET /insurance-providers` with no pagination.
  // Resource path is `insurance-providers` (hyphenated), not
  // `insurance_providers` — confirmed from the export's `query` field, not
  // a guess.
  // Also `apps/brands`' own resource (migration guide §6.2, M2 pilot) — the
  // export's `patchSelectedBrand`/`patchBrandFromTable` queries both repeat
  // `brand_id` in the PATCH body, same as `branches`' confirmed
  // `includeIdInPatchBody` requirement; not independently confirmed against
  // the live API here (no network access in this sandbox — see
  // apps/brands/MIGRATION-NOTES.md).
  brands: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
  },
  // Also its own first-class resource now (apps/insurance-providers, HATCH-52).
  // `updateInsuranceProviderLogo`'s body explicitly repeats
  // `insurance_provider_id` alongside `icon_image_id` even though the URL
  // already has the id — confirmed from the export's own query definition
  // (retool-exports/insurance-providers/queries.json), not a guess, hence
  // `includeIdInPatchBody: true` (same repeated-id-in-PATCH-body convention
  // `branches`/`brands` use above).
  'insurance-providers': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'insurance_provider_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
  },
  // Also `apps/brand-pages`' own resource (migration guide §6.2). Source
  // export's `getBrandPages` query has `paginationEnabled: false` and no
  // `limit`/cursor param at all — a bare, unpaginated `GET brand-pages`, same
  // "fetch the whole (small) collection at once" shape `domains`/`categories`/
  // `sections` above already use, hence `bareFilteredList` rather than the
  // project's cursor-pagination default. `updateBrandPageDetails`'s body
  // explicitly repeats `brand_page_id` even though the URL already has it —
  // confirmed from the export's own query definition, not a guess, hence
  // `includeIdInPatchBody: true` (same repeated-id-in-PATCH-body convention
  // `branches`/`brands` above use).
  'brand-pages': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_page_id',
    bareFilteredList: true,
    includeIdInPatchBody: true,
  },
  // Branch schedules (apps/branches' detail page, HATCH-24). Real path is
  // `schedules` (source export's `getSchedules`/`allSchedulesWithoutProductBound`
  // queries), always scoped by `?branch_id=` — reads the branch's schedules
  // and their embedded `days`, creates a default schedule for a location
  // type that doesn't have one yet (source export's `createDefaultSchedule`:
  // `POST schedules/` with `branch_id`/`location_type`), and updates
  // `manual_slots_number` (source export's `saveSlotsNumber`: `PATCH
  // schedules/{schedule_id}` with `schedule_id` repeated in the body — hence
  // `includeIdInPatchBody`). No delete is wired (out of this ticket's scope,
  // see MIGRATION-NOTES.md).
  schedules: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'schedule_id',
    bareFilteredList: true,
    listPath: 'schedules/',
    includeIdInPatchBody: true,
  },
  // A day-schedule row (a schedule's `days` array element) has no listing
  // endpoint of its own — it only ever arrives embedded in a `schedules` row
  // — so only `update` is used for this pseudo-resource, via `updatePath`'s
  // nested URL (source export's `updateDaySchedule`/`updateDay` queries:
  // `PATCH /schedules/{schedule_id}/day-schedule/{day_schedule_id}`). The API
  // 400s with "day_schedule_id field is missing" without it repeated in the
  // body too (confirmed in review) — same requirement as `schedules`'
  // `manual_slots_number` PATCH above.
  'day-schedules': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'day_schedule_id',
    includeIdInPatchBody: true,
    updatePath: (id, meta) => `/schedules/${(meta as { schedule_id: string })?.schedule_id}/day-schedule/${id}`,
  },
  // Branch-scoped form links (apps/branches' detail page, HATCH-24). Read at
  // `GET /form-links?branch_id=`, but created/deleted at the differently
  // prefixed `admin/form-links` — confirmed from the source export's
  // `branchLinkedForms`/`linkFormToBranch`/`deleteFormLinkFromBranch`
  // queries. No update endpoint exists (unlink + relink, never edit-in-place).
  'form-links': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'link_id',
    bareFilteredList: true,
    mutateResourcePath: 'admin/form-links',
  },
  // Form picker for the linked-forms table (source export's `getForms`):
  // plain, unpaginated `GET /dynamic-forms`, same shape as brands/
  // insurance-providers above. Also `apps/dynamic-forms`' own resource
  // (HATCH-122): `updateFormDetails` explicitly spreads `form_id` into its
  // PATCH body (`retool-exports/dynamic-forms/queries.json`), confirming the
  // HATCH-79 repeated-id-in-PATCH-body convention here too.
  'dynamic-forms': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'form_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
  },
  // A form's fields (`apps/dynamic-forms`' detail page, HATCH-122, folding in
  // `retool-exports/form-details` per the HATCH-103 tier-triage resolution).
  // No separate listing endpoint of its own — the source export's
  // `fieldsTable` only ever reads `getForm.data.fields`, an array embedded on
  // the parent form record — so `getList` fetches the form via `nestedIn`
  // instead. Create/update/delete go through the real, standalone
  // `createField`/`updateField`/`deleteField` endpoints (`POST fields` /
  // `PATCH fields/{id}` / `DELETE fields/{id}`), hence still its own
  // `RESOURCE_CONFIG` entry. `includeIdInPatchBody`: `updateFieldConfig`/
  // `addFieldChoice`/`deleteFieldChoice`/`updateFieldChoices` all explicitly
  // spread `field_id` into their PATCH body, confirming the same HATCH-79
  // convention as `dynamic-forms` above.
  fields: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'field_id',
    includeIdInPatchBody: true,
    nestedIn: { parentResource: 'dynamic-forms', parentIdFilter: 'form_id', field: 'fields' },
  },
  // Brand-scoped delivery methods (apps/brands' `BrandDetailPage`, HATCH-76
  // — source app's embedded "Brand Delivery Methods" module,
  // `retool-exports/brand-delivery-methods/`). Real path is
  // `delivery-methods`, always scoped by `?brand_id=` (the module's own
  // `getDeliveryMethods` query) — same `bareFilteredList` shape as
  // `schedules`/`form-links` above, not a resource meant to be browsed page
  // by page on its own (see MIGRATION-NOTES.md for the standalone,
  // unscoped `delivery-methods` app this ticket deliberately didn't build).
  // `includeIdInPatchBody`: per HATCH-79, default a new writable entry to
  // `true` unless confirmed otherwise — the standalone export's own `update`
  // query (`retool-exports/delivery-methods/queries.json`) explicitly
  // spreads `delivery_method_id` into the PATCH body, confirming this one.
  'delivery-methods': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'delivery_method_id',
    bareFilteredList: true,
    includeIdInPatchBody: true,
  },
  // Brand-scoped booking configs (apps/brands' `BrandDetailPage`, HATCH-332).
  // Confirmed against the live API spec (`oaq.py`): real base path is
  // `v2/admin/brand-booking-configs`, always scoped by `?brand_id=` on the
  // list read (same `bareFilteredList` shape `delivery-methods` above uses
  // for the same "always scoped to one brand" resource). `includeIdInPatchBody`
  // is confirmed, not inferred, this time — `PatchBrandBookingConfig`'s own
  // schema lists `brand_booking_config_id` as a *required* body field, the
  // strongest possible signal for the HATCH-79 repeated-id-in-PATCH-body
  // convention. `update`'s own default path ignores `mutateResourcePath`
  // (only `create`/`delete` fall back to it), hence the explicit `updatePath`.
  'brand-booking-configs': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_booking_config_id',
    bareFilteredList: true,
    listPath: 'v2/admin/brand-booking-configs',
    mutateResourcePath: 'v2/admin/brand-booking-configs',
    updatePath: (id) => `/v2/admin/brand-booking-configs/${id}`,
    includeIdInPatchBody: true,
  },
  // Region lookup for `DeliveryMethodsTable`'s `region_code` field (source
  // app's `getStates`: plain, unpaginated `GET /address-directory/`, same
  // shape as `brands`/`insurance-providers` above). `idField: 'code'` and
  // `optionText: 'name'` per the module's own `_columnOptionList` mapping
  // (`valueByIndex: item.code`, `labelByIndex` embeds `item.name`).
  // `listPath` needs the real trailing slash, same reason as `schedules`
  // above: the bare path 404s/redirects and the redirect is cross-origin, so
  // the browser blocks it as CORS before this code ever sees a response
  // (confirmed in review — the redirect was going unfollowed, leaving the
  // region list, and anything cascading off it like `city_code`, empty).
  'address-directory': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'code',
    fetchAllForGetMany: true,
    listPath: 'address-directory/',
  },
  // City lookup for `DeliveryMethodsTable`'s `city_code` field (source app's
  // `getCities`: `GET address-directory/{region_code}` — cities scoped to
  // whichever region is currently selected on the *same* row, not a plain
  // unfiltered list like `address-directory` above). Registered as its own
  // resource, distinct from `address-directory`, since the real endpoint
  // shape (and the `region_code` dependency) genuinely differs — same
  // "one physical API area, more than one RA resource" split `schedules`/
  // `day-schedules` already use. A function `listPath` bakes the row's
  // `region_code` into the path; `pathFilterField` keeps it out of both the
  // query string and the client-side filter match it'd otherwise also apply
  // against. Only fed through `EditableTable`'s `dependsOn` cascading-
  // reference input (`FieldConfig.reference.dependsOn`, `EditableTable.tsx`)
  // — never a generic `ReferenceField`/`getMany`, which has no per-row
  // `region_code` to scope by (see `DependentReferenceValue`,
  // `ResourceTable.tsx`).
  //
  // `fetchAllForGetMany` (not `bareFilteredList`, review: "filtering /
  // searching of name of city... using a network call - but API doesn't
  // support that - list of cities already fully returned - and has to be
  // filtered on frontend"): the real `GET address-directory/{region_code}`
  // endpoint takes no query params at all (confirmed against the live spec,
  // `AddressDirectoryEntry` is just `{ code, name }`) — it already returns
  // every city in the region in one response, the same "small, cacheable,
  // client-filterable lookup" shape `address-directory` (the region list)
  // above already uses, not a real server-side-searchable endpoint.
  // `bareFilteredList` sent `AutocompleteInput`'s `q` typed-search filter as
  // a real (unsupported, silently ignored) query param on every keystroke;
  // `fetchAllForGetMany`'s `fetchBareListCached` fetches each region's city
  // list once (cached per `listPath`, i.e. per region) and matches `q`
  // client-side instead, same as every other lookup-table resource.
  'address-directory-cities': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'code',
    fetchAllForGetMany: true,
    listPath: (filter) => `address-directory/${filter.region_code ?? ''}`,
    pathFilterField: 'region_code',
  },
  // Products (apps/products, HATCH-35). Confirmed in review: real
  // offset/limit pagination, offset a raw 0-based row index (not a page
  // number) — matches the source export's `getProduct` query
  // (`?...&limit=&offset={{ table1.pagination.offset || 0 }}`). The
  // free-text search box (`textInput1`) is a real filter sent as `?query=`,
  // not `?title=` — same `filterParamMap` pattern `branches`' `title` filter
  // uses.
  // HATCH-211: the list's `section_id` filter (a `filterable`
  // `ReferenceArrayInput`, always an array even for this single-valued
  // field — see `ResourceTable`'s filter-input note) was going out
  // comma-joined via `buildQuery`'s default, which the live API doesn't
  // split back into multiple ids — same shape `events`/`events-unconfirmed`/
  // `events-escalated`'s own `section_id` filter already needed
  // `explodeArrayParams` for (HATCH-121 round 5: repeated
  // `?section_id=a&section_id=b`, not `?section_id=a,b`). `categories`
  // (HATCH-227) needs the same treatment — confirmed via the API spec as a
  // repeated `?categories=uuid1&categories=uuid2`, same shape the ticket's
  // own example spells out.
  products: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'product_id',
    cursorField: 'product_id',
    paginationStyle: 'offset',
    filterParamMap: { title: 'query' },
    explodeArrayParams: ['section_id', 'categories'],
    // HATCH-1164 round 7 review: "you can use list product API to fetch all
    // linked products (using product_id filter - it accepts multiple
    // values - multiple product_id query params should be set)" — confirmed
    // via `oaq.py`: `GET /products?product_id=` is a real repeated-array
    // filter. Lets the content-management blocks editor's own product-block
    // preview resolve every linked product in one `getMany` call instead of
    // one `getOne` per block.
    getManyIdParam: 'product_id',
    // HATCH-216: `GET /products` accepts `?include_hidden=` — no
    // `filterParamMap` entry needed since `include_hidden` (`schema.ts`'s
    // filter-only field, `apps/products`' "Include hidden" checkbox) already
    // matches the real query param name, same as `branches` (HATCH-161).
    //
    // Confirmed in review (HATCH-41 round 5): the real `getOne` read is
    // `v2/admin/products/{id}`, not plain `products/{id}` — this is also
    // the endpoint that returns each option's nested `provider_price`, which
    // the product-detail page's options table (`options`' `nestedIn` config
    // above, since it fetches this same `products` record) needs to display
    // "Provider Price"/"Target Price"/"Provider Pricing". `patchProductRaw`
    // (the `update`/PATCH path) stays plain `products/{id}` — the two
    // endpoints' base paths genuinely differ, per the export.
    getOnePath: (id) => `/v2/admin/products/${id}`,
    // `table1`'s primary key (`product_id`) is always present in the
    // source's `patchProduct` changeset body — same repeated-id-in-PATCH-
    // body convention `branches`/`brands` confirmed/inferred; not
    // independently re-confirmed here either.
    includeIdInPatchBody: true,
  },
  // Add-on products' own picker list (HATCH-155, product-detail's
  // `addonsTable`/deferred "Add-on products" section — Tier-2 anti-pattern,
  // see `AddOnProductsSection.tsx`). Read-only (`AddOnProductsSection.tsx`
  // never edits a row here, only adds/removes an id from the parent
  // product's own `addon_ids`) — a separate pseudo-resource key rather than
  // a second config on `products` itself, same "one physical API area, more
  // than one RA resource" split `address-directory`/`address-directory-cities`
  // document.
  // **Review fix (HATCH-174 round 2):** the original `getAddOnProducts`
  // query (`GET products?limit=50&include_hidden=true&product_id={addon_ids}`)
  // sent `product_id` as a comma-joined param the live API rejected — round 1
  // fixed that with `explodeArrayParams`, but the ticket's follow-up feedback
  // redirected entirely: the API grew an `include_hidden` param on
  // `products/slim` (the same endpoint `slim-products` already uses for the
  // add-on *picker* dropdown), so both the picker and this display list now
  // read the one `products/slim?include_hidden=true` endpoint —
  // `fetchAllForGetMany` (a plain, unpaginated GET), filtered/reordered to
  // the caller's own `product_id: addonIds` filter client-side via the same
  // "exact set, in order" rule `category-banners` already documents, instead
  // of a server-side id-list query param. Kept as its own resource key
  // (rather than pointing `AddOnProductsSection.tsx` straight at
  // `slim-products`) since this list also needs `include_hidden` baked in,
  // which the shared `slim-products` config doesn't set — other consumers of
  // `slim-products` (`apps/doctor-profiles`, `apps/discount-codes`, etc.)
  // weren't asked to start including hidden products too.
  'addon-products': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'product_id',
    fetchAllForGetMany: true,
    listPath: 'products/slim?include_hidden=true',
  },
  // Also `apps/sections`' own resource (this ticket, HATCH-90). Confirmed
  // from the source export's `createSection`/`updateSection`/
  // `updateSectionIcons` queries (retool-exports/sections/app.json):
  // `updateSection`'s changeset body always carries `section_id`
  // (`table1.changesetArray[0]`, the row's own primary key), and
  // `updateSectionIcons` explicitly repeats `section_id` in its body even
  // though its URL already has it too (both PATCH the same
  // `sections/{section_id}`) — same repeated-id-in-PATCH-body convention
  // `branches`/`brands`/`categories` confirmed/inferred, hence
  // `includeIdInPatchBody: true`. `getSections`'s real list query is
  // `sections?include_hidden=true&from=retool` — `from=retool` is Retool's
  // own request-source instrumentation, not ported; `include_hidden=true`
  // is, via `listPath`, which also resolves `apps/products`'
  // previously-flagged "Known gap" (hidden sections missing from its
  // `section_id` reference dropdown — see apps/products/MIGRATION-NOTES.md)
  // for both consumers of this resource. `createSection` posts to the bare
  // `sections` path with no query string, hence the separate
  // `mutateResourcePath` rather than falling back to `listPath`.
  sections: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'section_id',
    fetchAllForGetMany: true,
    listPath: 'sections?include_hidden=true',
    mutateResourcePath: 'sections',
    includeIdInPatchBody: true,
  },
  // Also `apps/categories`' own resource (migration guide §6.2). The
  // export's `patchCategoriesRaw` query repeats `category_id` in its body
  // even though the URL already has it, same pattern `branches`/`brands`
  // confirmed/inferred; not independently confirmed against the live API
  // here (no network access in this sandbox — see
  // apps/categories/MIGRATION-NOTES.md).
  //
  // HATCH-243: real server-side offset/limit pagination, confirmed against
  // the live API spec (`oaq.py`): `GET /categories` takes `category_type`,
  // `parent_category_id`, `limit`, `offset` — same shape `products`' own
  // `paginationStyle: 'offset'` already uses. One resource, one config, used
  // everywhere (the table, every `reference: 'categories'` picker/filter) —
  // an earlier pass split this into two pseudo-resources (one paginated for
  // the table, one bare-fetch for pickers) but review asked for one shape
  // used consistently instead ("use one resource with pagination
  // everywhere... in dropdowns lets use big limit - 500"): every reference
  // picker/filter sets its own `perPage: 500` (`ReferenceFieldConfig.perPage`)
  // rather than this config branching on caller.
  categories: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'category_id',
    paginationStyle: 'offset',
    includeIdInPatchBody: true,
    // HATCH-247 (ticket wording: "add filter by title - use parameter
    // 'query' for API"): a real, server-side search param — `filterParamMap`
    // remaps ahead of `paginationStyle`'s own branch, so it applies to the
    // offset-paginated `getList` here the same way it would for any other
    // resource shape. `q` (HATCH-243 review, "same approach like we did for
    // users - 100 by default in dropdowns, then search using query param"):
    // `AutocompleteInput`/`AutocompleteArrayInput`'s own default
    // search-as-you-type filter key (`autocomplete-input.tsx`'s
    // `DefaultFilterToQuery`), sent whenever `categories` is used as a
    // `ReferenceInput`/`ReferenceArrayInput` target — same real `?query=`
    // param as the plain `title` filter above, same `branches`-established
    // `q: 'query'` convention (`dataProvider.ts`'s `branches` config).
    // Without this, a reference picker's search box sent an unmapped `?q=`
    // the live API silently ignores.
    filterParamMap: { title: 'query', q: 'query' },
    // This ticket (HATCH-283): a categories multiselect resolving many
    // selected ids (`ReferenceArrayField`) previously fired one `getOne` per
    // id via `getMany`'s generic fallback. Confirmed against the live API
    // spec (`oaq.py`): `GET /categories` takes `category_id` as a real
    // repeated-array filter (`?category_id=a&category_id=b`), so `getMany`
    // can resolve every requested id in one request instead.
    getManyIdParam: 'category_id',
  },
  // apps/domains (migration guide §6.2, Tier 1 per `migration-manifest.yaml`).
  // Source export's `table1`/`getDomains` query has `paginationEnabled: false`
  // and no `limit`/cursor param at all — a bare, unpaginated `GET domains`,
  // same "fetch the whole (small) collection at once" shape `categories`/
  // `sections` above already use, hence `bareFilteredList` rather than the
  // project's cursor-pagination default. `patchDomain`'s body
  // (`table1.changesetArray[0]`) references `.domain_id` directly
  // (`query: "domains/{{ table1.changesetArray[0].domain_id }}"`), confirming
  // the changeset object — and so the PATCH body — carries `domain_id`
  // alongside whatever fields actually changed, hence `includeIdInPatchBody`
  // (same repeated-id-in-PATCH-body convention `categories`/`brands` above
  // use). Not independently confirmed against the live API here (no network
  // access in this sandbox — see apps/domains/MIGRATION-NOTES.md).
  domains: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'domain_id',
    bareFilteredList: true,
    includeIdInPatchBody: true,
  },
  // Category detail page's three relational sections (HATCH-123,
  // `category-detail-manager`'s deferred children/products/banners
  // features, per `apps/categories/MIGRATION-NOTES.md`).
  //
  // Subcategories: real rows of the `categories` resource itself (a child
  // category is just another category with `parent_category_id` set), so
  // create/update reuse `categories`' own real endpoints. **Round 5 fix**
  // (ticket feedback: "child categories table is EMPTY... no request with
  // proper parameter in the dev tools"): round 3 claimed the real query was
  // `GET categories?parent_category_id=` but never actually sent it —
  // `fetchAllForGetMany` fetches the bare, unfiltered `/categories`
  // collection and filters in memory, so no request ever carried
  // `parent_category_id` at all, and — unlike `brands`/`categories`' own
  // list (small, genuinely-unpaginated resources) — the plain `categories`
  // collection is large enough that an unpaginated fetch doesn't reliably
  // return every row, silently dropping whatever children didn't happen to
  // land in whatever the bare request returned. `retool-exports/categories/
  // queries.json`'s `getSubCategories` query
  // (`"categories?{{ table1.selectedRowKey ? \"parent_category_id\" : null }}={{ table1.selectedRowKey }}"`)
  // confirms the real endpoint takes `parent_category_id` as a genuine
  // server-side filter — switched to `bareFilteredList` (same shape
  // `category-products`, below, already uses), which sends it as a real
  // `?parent_category_id=` query param instead of pretending to fetch
  // everything and filtering client-side.
  // `createChildCategory`'s body always adds `parent_category_id`
  // and inherits the parent's `category_type`; both are passed as this
  // pseudo-resource's `filter` (`CategoryChildrenSection.tsx`), which
  // `EditableTable`'s create already merges into every new row. `deleteCategory`
  // (the source export's only delete query, confirmed `v2/admin/categories/{id}`,
  // same `v2/admin/{resource}/{id}` shape `apps/users`' `deletePath` uses) is
  // new — `categories` itself doesn't currently expose delete anywhere else.
  'category-children': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'category_id',
    bareFilteredList: true,
    listPath: 'categories',
    mutateResourcePath: 'categories',
    updatePath: (id) => `/categories/${id}`,
    includeIdInPatchBody: true,
    deletePath: (id) => `/v2/admin/categories/${id}`,
  },
  // Products currently in this category — read-only here (no update/delete
  // path needed): adding goes through the bespoke `addCategoryToProduct`
  // endpoint (`v2/admin/categories/{category_id}/products`, bulk
  // `product_ids`) and removing PATCHes the *product's own* `categories`
  // array (`removeProductFromCategory`), neither a plain resource
  // create/delete — both are one-off `httpClient` calls in
  // `CategoryProductsSection.tsx`, not `RESOURCE_CONFIG` entries.
  //
  // NOT `nestedIn` (round 2 fix, ticket feedback: "no products shown for
  // category, while category has products already"): the source export's
  // `productsTable` reads `getCategoryDetails.data.products`, but
  // `getCategoryDetails` itself is `GET v2/public/categories/slug/{slug}` — a
  // separate, public storefront aggregation endpoint, not the plain admin
  // `GET categories/{category_id}` this app's `categories` resource actually
  // reads (no `getOnePath` override) for `CategoryDetailPage`'s own fields.
  // The admin endpoint never nests a `products` array, so treating this as
  // `nestedIn` (fetch the category, read `.products`) silently returned
  // nothing. The real, confirmed-working query for "products in category X"
  // is `apps/categories`' own `getProductsByCategory`
  // (`GET products?categories={category_id}`, `retool-exports/categories/
  // queries.json`) — already the native replacement this file's
  // `apps/categories/MIGRATION-NOTES.md` flagged for the plain `categories`
  // app's N+1 per-row anti-pattern. `bareFilteredList` (server-side filter,
  // one unpaginated page) against the real `products` list endpoint matches
  // that query exactly.
  'category-products': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'product_id',
    bareFilteredList: true,
    listPath: 'products',
    filterParamMap: { category_id: 'categories' },
  },
  // Banners currently linked to this category, in the category's own
  // `banner_ids` order. Also read-only: add/detach/reorder all PATCH the
  // parent `categories` record's `banner_ids` array directly
  // (`CategoryBannersSection.tsx`, via `useUpdate('categories', ...)`), same
  // "mutate the parent record, not a join resource" shape
  // `apps/branches`' `BranchSettingsSection` uses.
  //
  // **Round 3 fix** (ticket feedback: "category banners are empty"): this
  // was `nestedIn` off `categories`' own `GET categories/{category_id}`,
  // reading `.banners` — the same wrong assumption `category-children`/
  // `category-products` both made (see their own fix notes, above/below):
  // that binding is `getCategoryDetails.data.banners`, and `getCategoryDetails`
  // is the public/slug storefront aggregation endpoint, not the plain admin
  // one this app actually reads. The plain category record only ever carries
  // `banner_ids` (an id array, already in `categorySchema`, already the real
  // source of `bannerIds` below) — there's no admin endpoint that expands it
  // into full banner rows, so this fetches the same real, unfiltered
  // `content/banners` list `category-banner-options` already confirms works,
  // then filters/orders it to the caller's own `banner_id: bannerIds` filter
  // (`CategoryBannersSection.tsx`) via the `fetchAllForGetMany` "exact set, in
  // order" rule above.
  'category-banners': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'banner_id',
    fetchAllForGetMany: true,
    listPath: 'content/banners',
  },
  // The "pick a banner to add" source list for the section above — source
  // export's `getBanners`: a bare, unfiltered `GET content/banners`, unlike
  // `banners` above's product-page-scoped `?template_type=product_page`
  // (confirmed distinct from the export's own query, not a guess this
  // ticket merged the two). Flagged in MIGRATION-NOTES.md: the export
  // itself never scopes this to a "category page" template, so it may be
  // missing an equivalent `template_type` filter — confirm against the live
  // API before release.
  'category-banner-options': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'banner_id',
    fetchAllForGetMany: true,
    listPath: 'content/banners',
  },
  // Now also a real (not just reference-lookup) resource: `apps/personas`
  // (this ticket), the second, never-ported screen of the same source
  // export `apps/faqs` came from. `patchPesona`'s body
  // (`personasTable.changesetArray[0]`) always includes `persona_id` — it's
  // the table's `_primaryKeyColumnId`, which Retool's own changeset always
  // carries alongside whatever cells actually changed — so
  // `includeIdInPatchBody: true` per HATCH-79's backend-wide convention,
  // not independently confirmed against the live API (no network access in
  // this sandbox — see `apps/personas/MIGRATION-NOTES.md`).
  personas: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'persona_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
  },
  // FAQs (apps/faqs, HATCH-129). Source export's `getFAQs`/`getFilteredFAQs`:
  // `paginationEnabled: false`, a bare unpaginated `GET faqs`, with Retool's
  // own table doing any product/persona filtering client-side against that
  // same full list — same `fetchAllForGetMany` shape `personas`/`sections`
  // already use as a real (not just reference-lookup) resource. `patchFAQs`'s
  // body always repeats `faq_id`, same PATCH-id convention as
  // `branches`/`products`.
  faqs: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'faq_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
    // `getFilteredFAQs` (see `ResourceConfig.apiFilterKeys`): once either
    // filter is set, the real app queries `GET faqs?product_id=&persona_id=`
    // server-side rather than filtering the full list client-side.
    apiFilterKeys: ['product_id', 'persona_id'],
  },
  // Doctor FAQs (apps/doctor-faqs, HATCH-726, `v2/admin/content/doctor-faqs`,
  // confirmed via `oaq.py`) — a brand-new backend feature, no Retool source
  // app; distinct resource from `faqs` above (no product/persona scoping).
  // The real `GET` endpoint takes `offset`/`limit` (not the project-wide
  // cursor default), and `PatchDoctorFAQ`'s `doctor_faq_id` is a plain
  // required body field, same repeated-id-in-PATCH-body convention
  // `videos`/`branches`/`products` already use.
  'doctor-faqs': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'doctor_faq_id',
    paginationStyle: 'offset',
    listPath: 'v2/admin/content/doctor-faqs',
    getOnePath: (id) => `/v2/admin/content/doctor-faqs/${id}`,
    updatePath: (id) => `/v2/admin/content/doctor-faqs/${id}`,
    deletePath: (id) => `/v2/admin/content/doctor-faqs/${id}`,
    includeIdInPatchBody: true,
    apiFilterKeys: ['hidden', 'group_title'],
  },
  // `banner_id` reference picker (product-detail's `bannerSelect`, HATCH-41).
  // Source export's `getBanners`: `GET content/banners?template_type=product_page`
  // — a fixed, non-user-facing filter (every product's banner picker wants
  // the same `product_page` template), baked into `listPath` rather than a
  // real per-request filter param.
  banners: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'banner_id',
    fetchAllForGetMany: true,
    listPath: 'content/banners?template_type=product_page',
  },
  // Product options (product-detail page, HATCH-41 review round 2: "table
  // options loads options from product.options list — there is no separate
  // API for fetching options only"). Confirmed from the source export's
  // `createOption`/`createBookableOption`/`updateOption`/`deleteOption`
  // queries: reads are nested inside `getProduct`'s own response
  // (`nestedIn`), but writes go through the real, standalone `options`
  // endpoint (`POST options`, `PATCH options/{option_id}`,
  // `DELETE options/{option_id}`) — the default resource-name-shaped
  // create/update/delete paths already match this, no path override needed.
  //
  // `flattenRow` (round 4): each option's own `provider_price` sub-object
  // (`{ amount, recommended_price, provider: { rate, pricing_model } }`,
  // confirmed from the source export's `tableProductOptions`/`table3`
  // `_columnValueOverride`s) is surfaced as three flat columns —
  // `provider_price_amount` ("Provider Price", the one genuinely editable
  // one — see `ProductOptionsSection.tsx`'s `beforeSave`, which routes its
  // edits through the dedicated `v2/admin/provider-prices/by-option-fields`
  // endpoint instead of this resource's own PATCH, same as the source
  // export's `modifyOptionProviderAmount`/`modifyBookableOptionProviderAmount`
  // queries), `provider_price_target_price` ("Target Price", read-only), and
  // `provider_pricing` ("Provider Pricing", a read-only computed
  // `"{rate * 100}% {pricing_model}"` string). Replaces the previous,
  // separate `ProviderPricesSection`/`provider-prices` embedded table
  // (review round 4: "I don't need separate ProviderPricesSection — provider
  // price has to be configurable WITHIN ProductOptionsSection").
  options: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'option_id',
    nestedIn: { parentResource: 'products', parentIdFilter: 'product_id', field: 'options' },
    // `includeIdInPatchBody` (HATCH-79): this entry had the same gap as
    // `inclusions` below — editing a non-price option field (`hidden`,
    // `branch_extra_charge`, `processing_modes`, `user_id`, `brand_id`) PATCHed
    // `options/{option_id}` with no `option_id` in the body. Per the ticket,
    // the backend requires the id in the PATCH body project-wide, not just
    // for the resources independently confirmed against a source export.
    includeIdInPatchBody: true,
    flattenRow: (row) => {
      const providerPrice = row.provider_price as
        | { amount?: unknown; recommended_price?: unknown; provider?: { rate?: number; pricing_model?: string } }
        | null
        | undefined
      return {
        provider_price_amount: providerPrice?.amount ?? null,
        provider_price_target_price: providerPrice?.recommended_price ?? null,
        provider_pricing: providerPrice?.provider
          ? `${(providerPrice.provider.rate ?? 0) * 100}% ${providerPrice.provider.pricing_model ?? ''}`.trim()
          : null,
      }
    },
  },
  // Product inclusions (product-detail page, HATCH-65: "items bundled with
  // that product"). Reads are nested inside `getProduct`'s own response
  // (`ExtendedProduct.inclusions`, per the ticket: "already included in the
  // existing product-detail response — no new read endpoint is needed"),
  // same `nestedIn` shape `options` above uses; writes go through the real,
  // standalone `inclusions` endpoint (`restapi/app/routers/inclusions.py`) —
  // the default resource-name-shaped create/update/delete paths already
  // match, no path override needed. Reordering
  // (`ProductInclusionsSection.tsx`'s up/down actions) hits its own
  // dedicated `sort-order/{up|down}` endpoint directly via `httpClient`, not
  // through this data provider.
  //
  // `includeIdInPatchBody` (HATCH-79): missing on this entry, editing an
  // inclusion sent a PATCH body with no `inclusion_id` at all — confirmed
  // as a backend-wide convention (not just a `branches`/`brands`-style
  // per-resource quirk): "All patch requests require id to be set in body
  // and in most of the cases in the URL as well. That's a convention of
  // backend repo." Same fix applied to `options` below for the same reason.
  inclusions: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'inclusion_id',
    nestedIn: { parentResource: 'products', parentIdFilter: 'product_id', field: 'inclusions' },
    includeIdInPatchBody: true,
  },
  // Product sub-descriptions (HATCH-171, product-detail's
  // `table1SubDescriptions`/deferred "Sub-descriptions" section). Reads are
  // nested inside `getProduct`'s own response (`getProduct.data.descriptions`,
  // same `nestedIn` shape `inclusions`/`options` above use — no separate list
  // endpoint); create/update/delete go through the real, standalone
  // `descriptions` endpoint (source export's `createDescription`/
  // `updateDescription`/`deleteDescription` queries: `POST descriptions`,
  // `PATCH`/`DELETE descriptions/{description_id}`) — the default
  // resource-name-shaped paths already match, no override needed. Reordering
  // (`ProductSubDescriptionsSection.tsx`'s up/down actions) hits its own
  // dedicated `sort-order/{up|down}` endpoint directly via `httpClient`, same
  // as `inclusions`' own reorder buttons, not through this data provider.
  // `includeIdInPatchBody`: not independently confirmed for this resource
  // specifically (no network access in this sandbox), but every other
  // writable resource in this project follows the same backend-wide
  // convention (HATCH-79: "All patch requests require id to be set in body").
  descriptions: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'description_id',
    nestedIn: { parentResource: 'products', parentIdFilter: 'product_id', field: 'descriptions' },
    includeIdInPatchBody: true,
  },
  // Bundled products (HATCH-155, product-detail's `table4`/deferred "Bundled
  // products" section). Reads are nested inside `getProduct`'s own response
  // (`getProduct.data.bundled_products`, same `nestedIn` shape `options`/
  // `inclusions` above use — no separate list endpoint); create/update/delete
  // go through the real, standalone `bundled_products` endpoint (source
  // export's `addBundledProduct`/`patchBundledProduct`/`deleteBundledProduct`
  // queries: `POST bundled_products`, `PATCH`/`DELETE
  // bundled_products/{bundled_product_id}`) — the default resource-name-shaped
  // paths already match, no override needed. `includeIdInPatchBody`: not
  // independently confirmed for this resource specifically, but every other
  // writable resource in this project follows the same backend-wide
  // convention (HATCH-79: "All patch requests require id to be set in body").
  'bundled-products': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'bundled_product_id',
    mutateResourcePath: 'bundled_products',
    // `update`'s own default path ignores `mutateResourcePath` (it only
    // covers `create`/`delete`) and falls back to the plain
    // `/{resource}/{id}`, which would PATCH `/bundled-products/{id}` (the
    // hyphenated `RESOURCE_CONFIG` key, not a real endpoint) instead of
    // `/bundled_products/{id}` — confirmed live (HATCH-155 follow-up):
    // reviewer reported the PATCH hitting the wrong path prefix.
    updatePath: (id) => `/bundled_products/${id}`,
    nestedIn: { parentResource: 'products', parentIdFilter: 'parent_product_id', field: 'bundled_products' },
    includeIdInPatchBody: true,
  },
  // Specialist picker for a bookable option's `user_id` field
  // (product-detail's `getUsers`: plain `GET users/doctors`, same
  // fetch-everything shape as `brands`/`insurance-providers` above —
  // confirmed `paginationEnabled: false` in the source export).
  specialists: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'user_id',
    fetchAllForGetMany: true,
    listPath: 'users/doctors',
  },
  // Doctor-profiles (apps/doctor-profiles, HATCH-38). Confirmed from the
  // source export's `getProfiles`/`patchProfile`/`createProfile` queries.
  // Confirmed in review (PR #30): real offset/limit pagination, offset a raw
  // 0-based row index (not a page number), same shape as `products`; and the
  // list's free-text search is a real filter sent as `?query=`, not
  // `?title=` (there's no `title` query param on the real API) — same
  // `filterParamMap` pattern `branches`/`products` use.
  'doctor-profiles': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'doctor_profile_id',
    cursorField: 'doctor_profile_id',
    paginationStyle: 'offset',
    filterParamMap: { title: 'query' },
    // `patchProfile`'s body always includes `doctor_profile_id` (the
    // `table1.changesetArray[0]` spread already carries the row's primary
    // key) — same repeated-id-in-PATCH-body convention as `branches`/
    // `brands`/`products`; not independently confirmed against the live API
    // here either.
    includeIdInPatchBody: true,
  },
  // Doctor-user picker (source export's `getUsers`: plain, unpaginated `GET
  // users/doctors`, distinct from any general-purpose `users` resource that
  // might get its own path/config later) — used to resolve `user_id`
  // references. Real path diverges from this pseudo-resource's key, hence
  // `listPath` (see the `getMany` `listPath` fix above, which this resource
  // is the first to actually exercise).
  'doctor-users': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'user_id',
    fetchAllForGetMany: true,
    listPath: 'users/doctors',
  },
  // Slim product picker for `doctor-profiles`' `main_product_id` (source
  // export's `getSlimProducts`: plain, unpaginated `GET products/slim`,
  // distinct from the full paginated `products` resource apps/products
  // already owns).
  'slim-products': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'product_id',
    fetchAllForGetMany: true,
    listPath: 'products/slim',
  },
  // Products eligible as a doctor's `main_product_id` (HATCH-396: "limit
  // products to specializations in doctor profile") — the public storefront's
  // own specialist-specialization list (`GET v2/public/specialists/
  // specializations`, confirmed via `oaq.py`; despite the `specializations`
  // path segment it returns `SlimProduct` rows, same shape as `slim-products`
  // above), not the full unfiltered `products/slim` list.
  'specialization-products': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'product_id',
    fetchAllForGetMany: true,
    listPath: 'v2/public/specialists/specializations',
  },
  // Brand-type pickers for `doctor-profiles`' `university_id`/
  // `practice_clinic_id`/`association_id` references (HATCH-1282 follow-up:
  // "make it editable for admin user") — each is a plain, unpaginated `GET
  // brands?brand_type=…` scoped to the one `BrandType` (confirmed via
  // `oaq.py`) that field actually means, same
  // distinct-pseudo-resource-per-real-path convention `provider-brands`
  // above already uses for `brand_type=provider`.
  'university-brands': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_id',
    fetchAllForGetMany: true,
    listPath: 'brands?brand_type=study_place',
  },
  'practice-clinic-brands': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_id',
    fetchAllForGetMany: true,
    listPath: 'brands?brand_type=work_place',
  },
  'association-brands': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_id',
    fetchAllForGetMany: true,
    listPath: 'brands?brand_type=association',
  },
  // Doctor-scoped form links (apps/doctor-profiles' detail page). Unlike
  // `branches`' `form-links` (read at bare `GET /form-links`, written at
  // `admin/form-links`), the source export's `getDoctorForms`/
  // `linkFormToDoctor`/`unlinkForm` queries read AND write at the same
  // `admin/form-links` path — a distinct pseudo-resource key rather than a
  // second, conflicting config for `form-links`.
  'doctor-form-links': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'link_id',
    bareFilteredList: true,
    listPath: 'admin/form-links',
    mutateResourcePath: 'admin/form-links',
  },
  // Admin-facing completed-events list for a single doctor (HATCH-1224,
  // `apps/doctor-profiles`' new "Earnings" tab) — `GET
  // v2/admin/events/completed/{owner_id}` (confirmed via `oaq.py`,
  // `admin`-scoped, added by this ticket's own blocking backend ticket
  // HATCH-1220). Same shape as `specialists-portal`'s own
  // `specialists/earnings` resource (that app's doctor-scoped equivalent):
  // `owner_id` baked into the path (`pathFilterField`, same `specialist-events`
  // shape above) rather than sent as a query param, `before` doubles as the
  // pagination cursor while `after` stays the period filter.
  'doctor-earnings': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    cursorField: 'completed_at',
    cursorParam: 'before',
    listPath: (filter) => `v2/admin/events/completed/${filter.owner_id ?? ''}`,
    pathFilterField: 'owner_id',
  },
  // Specialists-review (apps/specialists-review, HATCH-94) — the onboarding
  // approval queue over the same `doctor-profiles` records `apps/doctor-profiles`
  // already owns — a distinct pseudo-resource key, same reasoning as
  // `doctor-users`/`slim-products` above, rather than a second, conflicting
  // `<Resource name="doctor-profiles">`. `only_not_approved=true` is
  // hardcoded into `listPath` (HATCH-94 review: this app should only ever
  // show not-yet-approved profiles, so there's no user-facing `is_approved`
  // filter to wire up — the server does the filtering).
  'specialists-review': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'doctor_profile_id',
    cursorField: 'doctor_profile_id',
    paginationStyle: 'offset',
    listPath: 'doctor-profiles?only_not_approved=true',
    getOnePath: (id) => `/doctor-profiles/${id}`,
    updatePath: (id) => `/doctor-profiles/${id}`,
    // Same repeated-id-in-PATCH-body convention as `doctor-profiles` above.
    includeIdInPatchBody: true,
  },
  // License-renewal-requests (apps/license-renewal-requests, HATCH-557,
  // unblocked by HATCH-560's `GET /v2/admin/license-renewal-requests`).
  // Confirmed offset/limit pagination via `oaq.py`. The real endpoint's own
  // pending-vs-history switch is `only_pending` (default true) — a `false`
  // value here means "show reviewed requests too", a real, different meaning
  // from every other boolean filter in this file, where `false` always means
  // "no restriction" (`buildQuery` drops a literal `false` by design for
  // those). Baked into `listPath` instead, same as `specialists-review`'s
  // own `only_not_approved=true` above.
  'license-renewal-requests': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'request_id',
    paginationStyle: 'offset',
    listPath: (filter) =>
      `v2/admin/license-renewal-requests${filter.include_reviewed ? '?only_pending=false' : ''}`,
  },
  // Orders (apps/orders, HATCH-36). `getOne`'s real path is `admin/orders/{id}`
  // (source export's `getOrder` query, retool-exports/order-detail/queries.json)
  // — a different base path than the list below, hence `getOnePath`.
  //
  // Real pagination shape confirmed from the source export's query template
  // (`getOrdersFiltered`, retool-exports/orders/queries.json — and its
  // `getOwnerOrders` sibling, which uses the identical scheme against a
  // different path, per MIGRATION-NOTES.md): `before` is the real pagination
  // cursor (the table's own `_nextAfterCursor` formula reads the previous
  // page's last row's `for_payment_since`, paginating backward through
  // time), seeded from the date-range filter's end on the first page;
  // `after` is a separate, constant, user-set date-range-start filter, never
  // touched by pagination — the reverse of this project's default `'cursor'`
  // convention (`?after={cursor}&limit=`), hence `cursorParam: 'before'`
  // (HATCH-36 review round 2).
  orders: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'order_id',
    cursorField: 'for_payment_since',
    cursorParam: 'before',
    listPath: 'orders/all/paid',
    getOnePath: (id) => `/admin/orders/${id}`,
  },
  // `apps/bookings-dashboard-internal` (HATCH-121). The same underlying
  // `events` resource `order-detail` (HATCH-42, `useOrderEvents.ts`) already
  // reads, but that ticket bypassed `dataProvider` entirely — its own
  // `events/by_order/{order_id}` is a bespoke, non-listable, per-order
  // endpoint, not a real browsable list. This is the first ticket that needs
  // a real, paginated `events` list, confirmed from
  // `retool-exports/bookings-dashboard-internal/queries.json`'s
  // `getConfirmedEvents`/`getEscalatedEvents` (`GET events/all`): same
  // `after`={real, constant date-range-start filter}/`before`={real
  // pagination cursor} split `orders` already uses, hence `cursorParam:
  // 'before'`. `cursorField: 'start_time'` — confirmed from
  // `confirmedBookingsTable`/`escalatedBookingsTable`'s own
  // `_nextAfterCursor` formula (`table.data[pageSize-1].start_time`), not the
  // `DEFAULT_RESOURCE_CONFIG`'s `created_at` (see `events-unconfirmed` below,
  // which is the one that actually wants the default). `id-in-path` writes
  // (`events/{id}/confirm`, `events/{id}/reschedule`) `order-detail`'s own
  // notes already name are unaffected by this entry — no `updatePath`/
  // `mutatePath` override needed since this app performs its writes via
  // dedicated `events/batch_update_admin`/`events/update_status`/
  // `events/{id}/confirm` calls (see `index.tsx`), not `dataProvider.update`.
  // **Review fix (round 5): `section_id` also needs repeated params.**
  // Reviewer confirmed the live API wants `?section_id=a&section_id=b` for
  // the section filter too, same shape `status` already needed — added to
  // `explodeArrayParams` alongside it rather than guessed from the export
  // (`sectionSelector.value` is templated directly into the query string
  // there, which doesn't show which serialization the endpoint expects).
  // **Review fix (round 7): confirmed's own status filter key.** Re-enabling
  // URL sync (`ResourceTable.disableSyncWithLocation` removed from all three
  // tabs, review feedback: "i'd like to keep navigation in url though")
  // brought back a variant of round 6's cross-tab leak: react-admin's URL
  // sync is global to the *page*, so all three tabs' filter values are one
  // shared JSON blob regardless of which tab's own filter form set them —
  // `index.tsx` now sends the confirmed tab's status filter under its own
  // field, `confirmed_status`, never the bare `status` every tab used to
  // fight over. `filterParamMap` renames it back to the real `status` query
  // param. Escalated shares this physical `events/all` endpoint but is its
  // own `RESOURCE_CONFIG` entry below (`events-escalated`) rather than a
  // second key mapped here — mapping both `confirmed_status` and
  // `escalated_status` onto this one entry's `status` would leave the two
  // tabs' requests racing (whichever key's leaked, stale value happens to
  // land last in the merged filter object wins), the exact "for_endorsement
  // is there, while it should not be there" bug reported this round, just
  // recreated one rename later — splitting the resource means a leaked
  // `escalated_status` in the confirmed tab's filter object has no mapping
  // in *this* entry and is simply never sent. `filterEmptyDefaults` restores
  // the confirmed tab's own full status set when its filter is cleared to
  // empty, instead of the request silently dropping the `status` param
  // entirely (`buildQuery` drops an empty array) and returning events of
  // every status.
  events: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    cursorField: 'start_time',
    cursorParam: 'before',
    listPath: 'events/all',
    explodeArrayParams: ['status', 'section_id'],
    filterParamMap: { confirmed_status: 'status' },
    // Mirrors `index.tsx`'s `CONFIRMED_STATUSES` — the confirmed tab's full
    // in-scope status set (`components.json`'s `confirmedStatuses`).
    filterEmptyDefaults: {
      confirmed_status: ['confirmed', 'completed', 'no_show', 'for_reschedule', 'canceled', 'escalated'],
    },
  },
  // Same physical `events/all` endpoint as `events` above (escalated and
  // confirmed are the same underlying `getConfirmedEvents`/
  // `getEscalatedEvents` API, differing only by which `status` values they
  // request) — its own `RESOURCE_CONFIG` entry rather than folded into
  // `events` so its own `escalated_status` filter key has an unambiguous
  // `filterParamMap`/`filterEmptyDefaults` target, isolated from the
  // confirmed tab's `confirmed_status` even when a page-global URL-synced
  // filter blob hands one tab's request the other tab's stale filter keys
  // too (see the `events` entry's note above) — same "one physical API
  // area, more than one RA resource" split `address-directory`/
  // `address-directory-cities`/`events`/`events-unconfirmed` already use,
  // just for tab isolation instead of a genuinely different endpoint path.
  'events-escalated': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    cursorField: 'start_time',
    cursorParam: 'before',
    listPath: 'events/all',
    explodeArrayParams: ['status', 'section_id'],
    filterParamMap: { escalated_status: 'status' },
    // The escalated tab has exactly one in-scope status; still shown as a
    // real (if single-choice) filter rather than a hidden fixed value
    // (review feedback: "please still show it, even if it's single one").
    filterEmptyDefaults: {
      escalated_status: ['escalated'],
    },
  },
  // Same physical `events` API area as `events` above, registered as its own
  // `RESOURCE_CONFIG` entry rather than folded into it — same "one physical
  // API area, more than one RA resource" split `address-directory`/
  // `address-directory-cities` already document — because the real endpoint
  // genuinely differs: `getUnconfirmedEvents` (`GET events/unconfirmed`, not
  // `events/all`). `cursorField` stays the `DEFAULT_RESOURCE_CONFIG` value
  // (`created_at`) — confirmed from `unconfirmedBookingsTable`'s own
  // `_nextAfterCursor` formula, unlike the `start_time` `events`/`events/all`
  // needs above.
  'events-unconfirmed': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    cursorParam: 'before',
    listPath: 'events/unconfirmed',
    explodeArrayParams: ['status', 'section_id'],
    // Same per-tab status filter rename as `events` above — `index.tsx`
    // sends this tab's status filter as `unconfirmed_status`.
    filterParamMap: { unconfirmed_status: 'status' },
    filterEmptyDefaults: {
      // Mirrors `index.tsx`'s `UNCONFIRMED_STATUSES`.
      unconfirmed_status: ['for_endorsement', 'endorsing', 'escalated'],
    },
  },
  // Also `apps/users`' own resource now (HATCH-100) — previously registered
  // only for the owner-search filter's lookup (apps/orders, HATCH-36 review
  // round 2), which assumed a plain, unpaginated `GET /users?search=`
  // (`bareFilteredList`) from that ticket's own copy of the query
  // (`retool-exports/orders/queries.json`). This ticket's own
  // `retool-exports/users/queries.json` `getUsers` query is the fuller
  // definition and confirms real forward-cursor pagination instead:
  // `users?search=&before=` — same "`before` is the real pagination cursor"
  // shape `orders`/`audit-log` already use (`table1`'s own `_nextAfterCursor`
  // formula reads the last row's `created_at`, matching `cursorField`'s
  // default), except the first page seeds `before` to a hardcoded
  // "tomorrow" sentinel rather than a real filter value — omitting it
  // entirely (this project's usual "no filter value yet" first-page
  // behavior) is assumed equivalent (no upper bound), not confirmed against
  // the live API. `bareFilteredList` dropped accordingly; `search` (not `q`)
  // stays the real free-text query param, still needed for the
  // `AutocompleteInput`-driven owner-search filters above (always send `q`).
  // `createUser` posts to the bare `users` path (matches the default), but
  // `removeUser` deletes at `v2/admin/users/{id}` — a different base path,
  // hence `deletePath`. `saveTableUpdates`'s PATCH body always carries
  // `user_id` (`{ user_id, ...table1.changesetArray[0] }`) — same
  // repeated-id-in-PATCH-body convention (HATCH-79) other resources use,
  // hence `includeIdInPatchBody`. No `getOne` query exists in the export
  // (the source app only ever reads `table1.selectedRow`) — the edit page's
  // fetch is an unconfirmed guess at the default `/users/{id}`, same
  // "no confirmed getOne" gap `audit-log` already flags.
  users: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'user_id',
    cursorParam: 'before',
    filterParamMap: { q: 'search' },
    includeIdInPatchBody: true,
    deletePath: (id) => `/v2/admin/users/${id}`,
  },
  // Providers (apps/providers). Confirmed from the source export's
  // `getProviders`/`createProvider`/`updateProvider` queries
  // (retool-exports/providers/queries.json): real base path is
  // `v2/admin/providers`, not the bare resource name, for both list and
  // mutations; `_serverPaginated: false` and no limit/cursor param on
  // `getProviders`, same bare-fetch-and-client-filter shape as
  // `brands`/`sections` above, not a browsable paginated list.
  // `updateProvider`'s body is the whole `changesetArray[0]` row, which
  // always carries `provider_id` — same repeated-id-in-PATCH-body
  // convention as `branches`/`brands`; not independently confirmed against
  // the live API here either.
  providers: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_id',
    fetchAllForGetMany: true,
    listPath: 'v2/admin/providers',
    updatePath: (id) => `/v2/admin/providers/${id}`,
    getOnePath: (id) => `/v2/admin/providers/${id}`,
    includeIdInPatchBody: true,
  },
  // Main-brand providers (HATCH-397, product-detail's
  // `SharedToSpecialistsProviderPriceSection.tsx`): a new endpoint (confirmed
  // via `oaq.py`) scoping the plain `providers` list above down to just the
  // providers linked to the marketplace's main brand — the only providers
  // eligible for a product's shared-to-specialists price. Bare, unpaginated
  // `GET`, same fetch-all-and-filter shape as `providers` itself. A separate
  // resource key rather than a `providers` filter, since the real endpoint
  // is its own path, not a query param on `GET /providers`.
  'main-brand-providers': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_id',
    fetchAllForGetMany: true,
    listPath: 'v2/admin/providers/main-brand',
  },
  // Provider prices (HATCH-397, product-detail's
  // `SharedToSpecialistsProviderPriceSection.tsx`): confirmed via `oaq.py` —
  // real base path `v2/admin/provider-prices` for list/create/update, same
  // repeated-id-in-PATCH-body convention (`includeIdInPatchBody`) as
  // `options`/`inclusions` above. `ProductOptionsSection.tsx`'s own
  // option-scoped provider prices go through this same real endpoint too
  // (`by-option-fields`), but never through this data-provider resource —
  // only the product-level (no `option_id`) row this section manages does.
  'provider-prices': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_price_id',
    listPath: 'v2/admin/provider-prices',
    mutateResourcePath: 'v2/admin/provider-prices',
    updatePath: (id) => `/v2/admin/provider-prices/${id}`,
    getOnePath: (id) => `/v2/admin/provider-prices/${id}`,
    includeIdInPatchBody: true,
  },
  // Provider integrations (apps/provider-integrations-management). Confirmed
  // from the source export's `providerIntegrations`/`createProviderIntegration`/
  // `patchProviderIntegration` queries: real base path is
  // `v2/admin/provider-integrations` for list, create, and update alike;
  // `paginationEnabled: false` and no limit/cursor param on the list query,
  // same bare-fetch-and-client-filter shape as `brands`/`providers` above.
  // No standalone single-record `GET` query exists in the export — this
  // ticket's `editStyle: page` needs one for the edit route's `getOne`, so
  // `getOnePath` mirrors `patchProviderIntegration`'s own URL shape
  // (`v2/admin/provider-integrations/{id}`), same inference `providers`'
  // own (unused) `getOnePath` above makes. `patchProviderIntegration`'s body
  // is the whole `changesetArray[0]` row, which always carries
  // `provider_integration_id` — same repeated-id-in-PATCH-body convention as
  // `branches`/`brands`/`providers`; not independently confirmed against the
  // live API here either.
  'provider-integrations': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_integration_id',
    fetchAllForGetMany: true,
    listPath: 'v2/admin/provider-integrations',
    updatePath: (id) => `/v2/admin/provider-integrations/${id}`,
    getOnePath: (id) => `/v2/admin/provider-integrations/${id}`,
    includeIdInPatchBody: true,
  },
  // A provider integration's mappings (apps/provider-integrations-management's
  // detail page, `providerMappingsSection`-style embedded table — mirrors
  // `apps/doctor-profiles`' `doctor-form-links` shape: a resource with no
  // browsable list of its own, always scoped by its owning parent's id).
  // Source export's `integrationMappings` query: `GET
  // v2/admin/provider-mappings?provider_integration_id=`, `_serverPaginated:
  // false` — a `bareFilteredList`, not a paginated resource. `createMapping`/
  // `patchMapping` share the same `v2/admin/provider-mappings` base path (no
  // separate `mutateResourcePath` needed, same as `ads-audiences` above).
  // `patchMapping`'s body is the whole `changesetArray[0]` row, which always
  // carries `provider_mapping_id` — same repeated-id-in-PATCH-body
  // convention as the resources above; not independently confirmed against
  // the live API. See MIGRATION-NOTES.md — `createMapping` exists in the
  // export but isn't wired to any UI trigger (the mappings table has no "Add
  // Row" toolbar button, unlike the provider-integrations table above), so
  // this ticket doesn't expose a create affordance for it either.
  'provider-mappings': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_mapping_id',
    bareFilteredList: true,
    listPath: 'v2/admin/provider-mappings',
    updatePath: (id) => `/v2/admin/provider-mappings/${id}`,
    includeIdInPatchBody: true,
  },
  // Provider-type brand picker for `provider-integrations`' `brand_id`
  // reference (source export's `providerBrands` query: `GET
  // brands?brand_type=provider`, distinct from the unfiltered `brands`
  // resource `apps/branches`/`apps/brands`/`apps/providers` already use for
  // their own `brand_id` references) — same
  // distinct-pseudo-resource-per-real-path convention `slim-products` uses
  // alongside the full `products` resource.
  'provider-brands': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'brand_id',
    fetchAllForGetMany: true,
    listPath: 'brands?brand_type=provider',
  },
  // Reviews (apps/reviews) — still no full inline edit (HATCH-51 review: no
  // moderation toggle for the whole row), but HATCH-99 adds back the one
  // write Retool had: publishing/unpublishing a review. The source export's
  // `updateReview` query (retool-exports/reviews/queries.json) confirms the
  // real PATCH path is nested under the review's owning user, not a bare
  // `/reviews/{id}` — `reviews/user/{user_id}/{review_id}`. `user_id` isn't
  // part of `update`'s `id`, so it travels via `meta` (same pattern as
  // `day-schedules`' `schedule_id` above). `includeIdInPatchBody` (HATCH-99
  // review): per HATCH-79, the backend requires the id repeated in the PATCH
  // body project-wide, not just for resources independently confirmed
  // against a source export — same fix as `inclusions`/`options` above. The
  // source table's own query ran unpaginated (`paginationEnabled: false`,
  // `_serverPaginated: false` — a bare `GET reviews`), so cursor pagination
  // against `/reviews?after=&limit=` here is this project's standard
  // convention, not independently confirmed against the real API — flagged
  // in MIGRATION-NOTES.md for reviewer confirmation, same as
  // `products`/`doctor-profiles` before their pagination style was confirmed.
  reviews: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'review_id',
    includeIdInPatchBody: true,
    updatePath: (id, meta) => `/reviews/user/${(meta as { user_id: string })?.user_id}/${id}`,
  },
  // Ads audiences (apps/ads-audiences, HATCH-74). The source export's
  // `getAudiences`/`createAudience` both hit the same `v2/admin/ad-audiences`
  // path (confirmed from the export, not a guess) — `listPath` alone covers
  // both `getList` and `create` (`update`'s `mutateResourcePath ?? listPath
  // ?? resource` fallback), no separate `mutateResourcePath` needed. Real
  // primary key is `ad_audience_mapping_id` (`table1Audiences`'s own
  // `_primaryKeyColumnId`), not `external_audience_id` (the id on the
  // external ad network). No `updatePath` — no update/PATCH query exists in
  // the export at all, hence `apps/ads-audiences`' `enableEdit={false}`.
  'ads-audiences': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'ad_audience_mapping_id',
    listPath: 'v2/admin/ad-audiences',
  },
  // Audit log (apps/audit-log) — read-only. The source export's only query
  // (`getAuditLogs`, retool-exports/audit-log/queries.json) reads
  // `admin/audit-logs` (plural, under `admin/`) — a different path than the
  // `audit-log` (singular) resource/directory name, hence `listPath`. Real
  // id field is `audit_log_id` (`table1`'s `_primaryKeyColumnId`), not `id`.
  // No `getOne` query exists anywhere in the export — the source drawer
  // reads the already-fetched row client-side, never a per-record fetch —
  // so `getOnePath` (needed for the detail page's `Show`) is an unconfirmed
  // guess mirroring `listPath`'s shape; flagged in MIGRATION-NOTES.md.
  //
  // Pagination (HATCH-77 review): the source query template reads
  // `admin/audit-logs?...&after={{ dateTime1.value }}&before={{
  // dateTime2.value }}&...` — the same `before`-is-the-cursor,
  // `after`-is-a-constant-range-start-filter shape `orders` above already
  // confirmed from its own identically-structured query template, not this
  // project's default `'cursor'` convention (plain `?after={cursor}`) —
  // hence `cursorParam: 'before'`.
  'audit-log': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'audit_log_id',
    cursorParam: 'before',
    listPath: 'admin/audit-logs',
    getOnePath: (id) => `admin/audit-logs/${id}`,
  },
  // Endorsement configuration (apps/endorsement-configuration). Real
  // primary key is `endorsement_config_id` (`tableEndorsementConfigs`'s own
  // `_primaryKeyColumnId`). `updateEndorsementConfig`'s body is the whole
  // `changesetArray[0]` row, which always carries `endorsement_config_id` —
  // same repeated-id-in-PATCH-body convention `branches`/`brands`/`providers`
  // use above (HATCH-79: a backend-wide convention, not a per-resource
  // quirk) — not independently confirmed against the live API here.
  'endorsement-configs': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'endorsement_config_id',
    includeIdInPatchBody: true,
  },
  // Discount codes (apps/discount-codes, HATCH-83). The source export's own
  // `getCodes` query is a bare, unpaginated `GET discount-codes`
  // (`paginationEnabled: false`) with no `before`/`limit`/`query` params
  // anywhere — `cursorParam: 'before'` and `filterParamMap` below are built
  // directly from the reviewer's ticket comment ("please add to table query
  // filter and cursor based pagination (before/limit)"), not confirmed
  // against a live API response; see apps/discount-codes/MIGRATION-NOTES.md.
  // `includeIdInPatchBody` follows the HATCH-79 project-wide PATCH-id
  // convention (the source `patchCodes` query's body, `table1.
  // changesetArray[0]`, always carries `discount_code_id`).
  'discount-codes': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'discount_code_id',
    cursorParam: 'before',
    filterParamMap: { code: 'query' },
    includeIdInPatchBody: true,
  },
  // Cart-based discounts (apps/cart-based-discounts, HATCH-140) — distinct
  // from `discount-codes` above (see schema.ts's relationship-check note).
  // The source export's own `getDiscounts` query is a bare, unpaginated
  // `GET cart-discounts` (`paginationEnabled: false`) — same fetch-everything
  // anti-pattern `discount-codes` originally flagged for its own `getCodes`;
  // not replicated here either, defaulting to the project's standard cursor
  // pagination instead, unconfirmed against the live API (no `getOne`/
  // pagination query in the export to confirm against — see
  // MIGRATION-NOTES.md). `patchDiscount`'s body always carries `discount_id`
  // (HATCH-79 convention).
  'cart-discounts': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'discount_id',
    includeIdInPatchBody: true,
  },
  // A cart-discount's rules — no listing endpoint of its own, only ever
  // arriving nested in `tableCartDiscounts.selectedRow.rules`
  // (`cart-based-discounts/CartDiscountRulesSection.tsx`), hence `nestedIn`.
  // Create/update/delete go through their own real endpoints: `createRule`
  // POSTs to `cart-discounts/{discount_id}/rules` (the parent id in the URL,
  // not just the body — `createPath`, since `mutateResourcePath` alone can't
  // express a base that differs between create and delete); `patchRule`/
  // `deleteRule` both use the flat `cart-discounts/rules/{rule_id}` base
  // (`updatePath` + `mutateResourcePath`, since `update`'s own default path
  // ignores `mutateResourcePath` and instead falls back to the plain
  // resource name). `includeIdInPatchBody`: `patchRule`'s body always
  // repeats `rule_id` (HATCH-79 convention).
  'cart-discount-rules': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'rule_id',
    includeIdInPatchBody: true,
    nestedIn: { parentResource: 'cart-discounts', parentIdFilter: 'discount_id', field: 'rules' },
    updatePath: (id) => `/cart-discounts/rules/${id}`,
    mutateResourcePath: 'cart-discounts/rules',
    createPath: (data) => `/cart-discounts/${data.discount_id}/rules`,
  },
  // Permissions (apps/permissions, HATCH-91). The source export's own
  // `permissions` query is a bare, unpaginated `GET permissions`
  // (`paginationEnabled: false`, no query params at all) — same
  // fetch-everything shape `brands`/`sections`/`categories` already use for
  // their own main list, not a resource meant to be browsed page by page.
  // `createPermission`/`updatePermission`/`deletePermission` all confirm the
  // default resource-name-shaped paths (`permissions`, `permissions/{id}`),
  // so no `mutateResourcePath`/`updatePath` override is needed.
  // `includeIdInPatchBody` follows the HATCH-79 project-wide PATCH-id
  // convention (`updatePermission`'s body is `changesetArray[0]`, which always
  // carries the row's primary key, `permission_id`) — not independently
  // confirmed against the live API here, see MIGRATION-NOTES.md.
  permissions: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'permission_id',
    fetchAllForGetMany: true,
    includeIdInPatchBody: true,
  },
  // User picker for `permissions`' `user_id` field. Review feedback (HATCH-91
  // round 2): `permission_scope` is *required* by this endpoint, not an
  // optional page-level toggle — `GET users` with no `permission_scope`
  // param 400s. So this is a `bareFilteredList` resource scoped by the
  // permission's own `scope` field, same cascading-reference shape
  // `address-directory-cities`'/`region_code` uses (`ReferenceFieldConfig.
  // dependsOn`), not a plain fetch-everything resource — the choice list
  // must always carry a `scope` (defaulting to `"branch"`, same default the
  // source app's `scopeSelector` and this app's create form already use).
  // `filterParamMap` renames the generic `dependsOn`/manual-filter key
  // `scope` to the real `permission_scope` query param. A distinct key from
  // the existing `users` resource (apps/orders' owner search, `?search=`,
  // `bareFilteredList`) since the two apps' real query shapes genuinely
  // differ — see MIGRATION-NOTES.md. `getOnePath` keeps the real `/users/{id}`
  // path now that `getMany`'s fetch-everything shortcut no longer applies.
  'permission-users': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'user_id',
    bareFilteredList: true,
    listPath: 'users',
    getOnePath: (id) => `/users/${id}`,
    filterParamMap: { scope: 'permission_scope', q: 'search' },
  },
  // Triggers (apps/triggers, HATCH-119; pagination + `signal_type` filter
  // shape confirmed against the live API spec per HATCH-308) — own CRUD
  // confirmed independent of `flows`/`dynamic-forms` (HATCH-114 triage).
  // Real base path is `admin/triggers`, not the bare `triggers` the
  // `<Resource name="triggers">` key would default to, hence the explicit
  // `listPath`/`mutateResourcePath`/`getOnePath`/`updatePath` below.
  // `GET admin/triggers` takes real `?offset=&limit=` pagination (confirmed
  // against the live API spec, not the bare unpaginated fetch
  // MIGRATION-NOTES.md previously guessed at) — same shape `products`/
  // `categories` already use `paginationStyle: 'offset'` for.
  // `explodeArrayParams`: the real `signal_type` query param is a
  // `List[SignalType]`, sent as repeated `?signal_type=a&signal_type=b`
  // (confirmed against the live API spec), same shape `events`' `status`
  // filter already needed this for.
  // `includeIdInPatchBody` follows the HATCH-79 project-wide PATCH-id
  // convention (`patchTrigger`'s body is `changesetArray[0]`, which always
  // carries `trigger_id`).
  triggers: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'trigger_id',
    cursorField: 'trigger_id',
    paginationStyle: 'offset',
    includeIdInPatchBody: true,
    listPath: 'admin/triggers',
    mutateResourcePath: 'admin/triggers',
    getOnePath: (id) => `/admin/triggers/${id}`,
    updatePath: (id) => `/admin/triggers/${id}`,
    explodeArrayParams: ['signal_type'],
  },
  // Signal-type picker for `triggers`' `signal_type` field (HATCH-308): the
  // real, authoritative signal list comes from `GET admin/triggers/signals`,
  // not the static fallback enum `schema.ts` previously hardcoded (which
  // had drifted from the live `SignalType` enum) — same fetch-everything
  // lookup shape `programs`/`html-content`/`workflows` below already use.
  // Each entry's own `signal_type` doubles as both id and display value —
  // `SignalDashboardContext` (the real response shape) has no separate
  // label field.
  signals: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'signal_type',
    fetchAllForGetMany: true,
    listPath: 'admin/triggers/signals',
  },
  // Program picker for `triggers`' `action_program_id` (source export's
  // `getPrograms`: plain, unpaginated `GET content/programs`, same
  // fetch-everything shape `dynamic-forms`/`sections` already use). Not a
  // full `<Resource>` app — this ticket's scope is `triggers` only.
  programs: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'program_id',
    fetchAllForGetMany: true,
    listPath: 'content/programs',
  },
  // Content picker for `triggers`' `action_content_id`, the linked article
  // read/write endpoint for `apps/blog`'s detail screen (HATCH-645), and now
  // (HATCH-1061) `apps/content-management`'s own list+edit+create app over
  // this same resource. `getOnePath`/`updatePath`/`mutateResourcePath`
  // confirmed via `oaq.py`'s `content-html-content` tag (`GET`/`PATCH
  // content/html-content/{content_id}`).
  // `PatchHtmlContent` has no `content_id` body field at all (confirmed via
  // `oaq.py`) — this project's usual `includeIdInPatchBody` convention
  // doesn't apply here, unlike every other writable resource in this file.
  // No longer `fetchAllForGetMany` (HATCH-1061): the real list endpoint is a
  // genuine `after`/`before`/`limit`/`query` cursor-paginated,
  // server-searched one (confirmed via `oaq.py`) — matches
  // `DEFAULT_RESOURCE_CONFIG`'s own cursor defaults exactly, so no override
  // needed there either. `q: 'query'` keeps `triggers`' own
  // `AutocompleteInput` picker (whose typeahead sends `?q=`) searching
  // server-side too, same `filterParamMap` convention `branches`/
  // `doctor-profiles` already use.
  'html-content': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'content_id',
    filterParamMap: { q: 'query' },
    listPath: 'content/html-content',
    mutateResourcePath: 'content/html-content',
    getOnePath: (id) => `/content/html-content/${id}`,
    updatePath: (id) => `/content/html-content/${id}`,
  },
  // Read-only file lookup by id (HATCH-1164 round 7 review: "i've rolled get
  // by file_id admin API - use it to get files" — for the content-management
  // blocks editor's own image-block preview, so it can show the real image
  // right away instead of waiting on a save + the record's own
  // `blocks_expanded`). No list/create/update path exists for this resource
  // on the real API (confirmed via `oaq.py`) — only `GET
  // v2/admin/content/files/{file_id}`, so this is `getOne`-only; `getMany`
  // (multiple image blocks, each with a different `file_id`) falls back to
  // the generic per-id `getOne` fallback every other id-only resource here
  // uses, since there's no real multi-id filter on this endpoint to opt into
  // via `getManyIdParam`.
  'content-files': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'file_id',
    getOnePath: (id) => `/v2/admin/content/files/${id}`,
  },
  // Workflow picker for `triggers`' `action_workflow_id` (source export's
  // `getWorkflows`: plain, unpaginated `GET v2/admin/workflows/` — trailing
  // slash preserved from the confirmed query path, same
  // redirect-avoidance reasoning `schedules`/`address-directory` document
  // above).
  workflows: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'workflow_id',
    fetchAllForGetMany: true,
    listPath: 'v2/admin/workflows/',
  },
  // Same `GET v2/admin/workflows/` list as `workflows` above, but keyed by
  // `slug` instead of `workflow_id` — for `triggers`' `match_workflow_slug`
  // (HATCH-308 round 3, ticket: "use this api - and do dropdowns for
  // workflow slug/step slug instead of plain inputs"), which stores the
  // slug, not the UUID `workflows` is keyed by. A distinct resource key
  // rather than an `optionValue` override on `workflows` itself, since
  // `action_workflow_id`'s existing `ReferenceInput` still needs the
  // UUID-keyed one — same "one physical API area, more than one RA
  // resource" split `schedules`/`day-schedules` already use.
  'workflow-slugs': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'slug',
    fetchAllForGetMany: true,
    listPath: 'v2/admin/workflows/',
  },
  // `nestedIn` parent for `workflow-steps` below — only ever reached
  // through that resource's own `getOnePath` lookup (`GET
  // v2/admin/workflows/{workflow_slug}`, confirmed against the live API
  // spec), never listed/fetched directly as its own resource.
  'workflow-detail': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'slug',
    getOnePath: (slug) => `/v2/admin/workflows/${slug}`,
  },
  // A workflow's own steps (HATCH-308 round 3) — for `triggers`'
  // `match_to_step_slug` picker, scoped to whichever workflow slug is
  // currently selected (`EditableTable`'s `dependsOn` cascading-reference
  // shape, `WorkflowStepSlugInput` in `apps/triggers/index.tsx`). No
  // separate listing endpoint of its own — `GET
  // v2/admin/workflows/{workflow_slug}`'s response nests `steps` alongside
  // the workflow and its transitions, so `getList` fetches that one record
  // via `nestedIn` and reads its `steps` array, same shape `fields`/
  // `options`/`inclusions` (products/dynamic-forms) already use for a
  // nested-array pseudo-resource.
  'workflow-steps': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'slug',
    nestedIn: { parentResource: 'workflow-detail', parentIdFilter: 'workflow_slug', field: 'steps' },
  },
  // Holiday-config entries (apps/calendar, folded in per HATCH-53 — see
  // `apps/calendar/MIGRATION-NOTES.md`). Confirmed from the dedicated
  // `holiday-calendar` export's `getHolidayConfig`/`createHolidayEvent`/
  // `updateHolidayEvent` queries: `admin/events/holiday-config`, range
  // query via `?after=&before=` plus optional `brand_id`/`branch_id`/
  // `owner_id` filters (already-matching param names, no `filterParamMap`
  // needed), and `updateHolidayEvent`'s body repeats `event_id` — same
  // repeated-id-in-PATCH-body convention as `branches`/`brands` above.
  // No `getOnePath`: a 2026-07-28 live-API check confirmed
  // `/admin/events/holiday-config/{id}` (the export's own list/update path
  // shape, this override's previous guess) 404s — there's no dedicated
  // single-record `GET` at all. Rather than leave a known-wrong override any
  // future `getOne` caller could still hit, it's omitted entirely: this
  // resource is only ever edited via `apps/calendar/HolidaySidePanel`, from
  // the record `ResourceCalendar` already fetched for the visible window,
  // never via a `getOne`-driven edit route. `deletePath` mirrors the same
  // list/update path shape and is still unconfirmed the same way — the
  // live-API check above only exercised `GET`, not `DELETE`.
  'holiday-config': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    listPath: 'admin/events/holiday-config',
    mutateResourcePath: 'admin/events/holiday-config',
    updatePath: (id) => `/admin/events/holiday-config/${id}`,
    deletePath: (id) => `/admin/events/holiday-config/${id}`,
    rangeParams: { start: 'after', end: 'before' },
    includeIdInPatchBody: true,
  },
  // `apps/form-submissions` (HATCH-138). `getList`'s only query
  // (`formSubmissions`) sends its cursor under `before`, not this project's
  // default `after` — same shape `orders`/`audit-log` already use, hence
  // `cursorParam: 'before'`. Unlike those, `before` isn't a user-facing
  // filter here — there's no such input in `apps/form-submissions`' table —
  // so it's never seeded from `filter['before']`; the export's own query
  // template defaults it to `moment().format()` on the first page
  // (`before={{ currentPage == 0 ? moment().format() : afterCursor }}`),
  // and the real API 400s if `before` is missing entirely, hence
  // `cursorDefault`. `patchFormSubmission`'s body explicitly repeats
  // `submission_id` alongside `user_id`/`form_slug`/`data`, hence
  // `includeIdInPatchBody`.
  'form-submissions': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'submission_id',
    cursorParam: 'before',
    cursorDefault: () => new Date().toISOString(),
    includeIdInPatchBody: true,
  },
  // `apps/medical-record-sections` (HATCH-144) — a distinct resource from
  // `sections` above (Storefront product sections, `/sections`) despite the
  // similar name/shape; see MIGRATION-NOTES.md's relationship check. Real
  // endpoint is `/records/sections`, confirmed from the source export's
  // `getSections` query (`retool-exports/medical-record-sections/
  // queries.json`); `createSection`/`patchSection` write to
  // `admin/records/sections`(`/{section_id}`), same
  // Retool-instrumentation-prefix-on-writes shape `sections`' own
  // `admin/records/sections` (create) vs. bare `records/sections` (read)
  // doesn't otherwise resemble, so both a `listPath` and
  // `mutateResourcePath` override are needed. `patchSection`'s changeset
  // body always carries `section_id` (`table1.changesetArray[0]`), hence
  // `includeIdInPatchBody`. The source export's own `deleteSection` query
  // targeted `admin/triggers/{{ table1.selectedRow.section_id }}` — the
  // `triggers` resource's own delete path, a copy-paste bug in the source
  // Retool app (see MIGRATION-NOTES.md) — not replicated. `deletePath`
  // below is instead confirmed against the live API spec (HATCH-186):
  // `DELETE /api/admin/records/sections/{section_id}` is a real,
  // admin-scoped operation on this same resource, mirroring create/update.
  'medical-record-sections': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'section_id',
    listPath: 'records/sections',
    mutateResourcePath: 'admin/records/sections',
    updatePath: (id) => `/admin/records/sections/${id}`,
    deletePath: (id) => `/admin/records/sections/${id}`,
    includeIdInPatchBody: true,
  },
  // A doctor's own booked events (HATCH-154's `specialists-calendar` app and
  // `apps/doctor-profiles`' new "Calendar" tab — both via the shared
  // `SpecialistCalendar` component). The ticket names `events/by_user/{user_id}`
  // directly; no retool export anywhere uses that exact bare shape (the
  // closest is `order-detail`'s item-scoped
  // `events/by_user/{owner_id}/order_item_id/{order_item_id}`, a different,
  // narrower endpoint) — unconfirmed against the live API, flagged in
  // `apps/specialists-calendar/MIGRATION-NOTES.md` for a human to verify.
  // `owner_id` is baked into the path (`pathFilterField`, same shape
  // `address-directory-cities`' `region_code` uses) rather than sent as a
  // query param. `rangeParams` mirrors `events`/`holiday-config`'s own
  // confirmed `?after=&before=` convention for a calendar-style visible-window
  // fetch — also unconfirmed for this specific endpoint.
  'specialist-events': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'event_id',
    listPath: (filter) => `events/by_user/${filter.owner_id ?? ''}`,
    pathFilterField: 'owner_id',
    rangeParams: { start: 'after', end: 'before' },
  },
  // Videos (apps/videos, HATCH-501). Confirmed via `oaq.py`: real base path
  // is `v2/admin/content/videos` (this app's own list/create endpoint isn't
  // reachable at the bare `videos` resource name at all). List/getOne/update/
  // delete all fit this project's generic CRUD shape; `create` doesn't — the
  // real `POST v2/admin/content/videos` returns a `VideoUpload` envelope
  // (`{video, upload_url, expires_at}`), not a bare `ExtendedVideo`, and the
  // file-upload flow needs a second, non-JSON request (the returned
  // `upload_url`) before the record is actually usable — `apps/videos/index.tsx`'s
  // create page calls `httpClient` directly instead of `dataProvider.create`,
  // same "bypass the generic create for a resource whose real create
  // response doesn't fit" reasoning already applied project-wide for a
  // non-CRUD action (see `ProductInclusionsSection.tsx`'s reorder button).
  //
  // `explodeArrayParams: ['tags']`: the live spec models the list's `tags`
  // filter as a JSON request body on the `GET` (`requestBody.tags`), not a
  // query param — impossible to send from a browser `fetch` (a `GET` with a
  // body throws). Sent as a repeated `?tags=` query param instead, same
  // shape every other array filter in this project uses; flagged here as
  // unconfirmed against the real backend, since the live spec's own
  // modeling suggests it may only read `tags` from the body.
  videos: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'video_id',
    listPath: 'v2/admin/content/videos',
    mutateResourcePath: 'v2/admin/content/videos',
    getOnePath: (id) => `/v2/admin/content/videos/${id}`,
    updatePath: (id) => `/v2/admin/content/videos/${id}`,
    deletePath: (id) => `/v2/admin/content/videos/${id}`,
    explodeArrayParams: ['tags'],
    filterParamMap: { title: 'query' },
    // HATCH-501 review (Alex Gridnev): "when do patch you must include
    // video_id to payload" — confirmed via `oaq.py`'s `PatchVideo` schema,
    // `video_id` is a plain *required* body field (no `anyOf null`), the
    // strongest form of the HATCH-79 project-wide repeated-id-in-PATCH-body
    // convention every other writable resource in this file already sets.
    includeIdInPatchBody: true,
  },
  // Prescription-blocked provider transactions (apps/provider-transactions,
  // HATCH-606). Brand-new feature, no Retool source. Confirmed via `oaq.py`:
  // real base path `v2/admin/provider-transactions`, id field
  // `provider_transaction_id`, and `?before=&after=&limit=` — same
  // `before`-cursor/constant-`after`-filter split `orders`/`specialist-events`
  // above already use, hence `cursorParam: 'before'`.
  'provider-transactions': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'provider_transaction_id',
    cursorParam: 'before',
    listPath: 'v2/admin/provider-transactions',
  },
  // Blogs (apps/blog, HATCH-644/HATCH-645). Confirmed via `oaq.py`: real base
  // path is `v2/admin/content/blogs` (this app's own list/create endpoint
  // isn't reachable at the bare `blogs` resource name at all), same shape
  // `videos` above already established for this project's other brand-new
  // (no Retool source) content-type apps. `getOnePath` now backs the detail
  // screen's edit route (HATCH-645). Real primary key is `blog_id`, not
  // `content_id` (the linked content row's own id, also present on every
  // response) — `GET`/`PATCH .../{blog_id}` confirmed via `oaq.py`.
  blogs: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'blog_id',
    listPath: 'v2/admin/content/blogs',
    mutateResourcePath: 'v2/admin/content/blogs',
    getOnePath: (id) => `/v2/admin/content/blogs/${id}`,
    // `update`'s own default path ignores `mutateResourcePath` (only
    // `create`/`delete` fall back to it) — without this, saving a blog PATCHed
    // the bare `/blogs/{id}`, which 404s (HATCH-655).
    updatePath: (id) => `/v2/admin/content/blogs/${id}`,
    // The list's free-text search box is a real filter sent as `?query=`
    // (title+description ILIKE per the ticket), not `?title=` — same
    // `filterParamMap` pattern `branches`/`products`/`videos` use.
    filterParamMap: { title: 'query' },
    // `PatchBlog`'s `blog_id` is a plain *required* body field (confirmed via
    // `oaq.py`, no `anyOf null`) — same strongest form of the HATCH-79
    // repeated-id-in-PATCH-body convention `videos` above already sets.
    // Unexercised until now (no edit page existed before HATCH-645).
    includeIdInPatchBody: true,
  },
  // Promos (apps/promos, HATCH-781). Confirmed via `oaq.py`: real base path
  // is `v2/admin/content/promos` (this app's own list/create endpoint isn't
  // reachable at the bare `promos` resource name at all), same shape
  // `videos`/`blogs` above already established for this project's other
  // brand-new (no Retool source) content-type apps. The live `GET` endpoint
  // takes no query params at all (confirmed via `oaq.py` — no `limit`/
  // `offset`/`after` anywhere in its spec), a real, small, fully-returned
  // collection, hence `bareFilteredList` rather than this project's default
  // cursor pagination — same shape `brand-pages`/`domains`/`sections` above
  // already use for the same "fetch the whole (small) list" case.
  // `PatchPromo`'s `promo_id` is a plain *required* body field (confirmed via
  // `oaq.py`, no `anyOf null`) — same strongest form of the HATCH-79
  // repeated-id-in-PATCH-body convention `videos`/`blogs` above already set.
  promos: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'promo_id',
    bareFilteredList: true,
    listPath: 'v2/admin/content/promos',
    mutateResourcePath: 'v2/admin/content/promos',
    getOnePath: (id) => `/v2/admin/content/promos/${id}`,
    updatePath: (id) => `/v2/admin/content/promos/${id}`,
    deletePath: (id) => `/v2/admin/content/promos/${id}`,
    includeIdInPatchBody: true,
  },
  // A promo's linked home-page banners (HATCH-1064, `PromoBannersSection.tsx`),
  // in the promo's own `home_page_banner_ids` order. Unlike `category-banners`
  // (no dedicated expand endpoint existed for that ticket, so it fetches
  // every banner and filters/orders client-side), the live API has a real
  // per-promo endpoint that already returns the expanded rows in order —
  // confirmed via `oaq.py`: `GET v2/admin/content/promos/{promo_id}/banners`
  // -> `ExpandedBanner[]` — so this is scoped server-side via a function
  // `listPath` + `pathFilterField`, same shape `address-directory-cities`
  // above already uses for a parent-id-scoped nested list. Read-only (no
  // update/create/delete endpoint of its own): add/detach/reorder all PATCH
  // the parent `promos` record's own `home_page_banner_ids` array instead
  // (`PromoBannersSection.tsx`, via `useUpdate('promos', ...)`), same
  // "mutate the parent record, not a join resource" shape `category-banners`
  // documents.
  'promo-banners': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'banner_id',
    bareFilteredList: true,
    listPath: (filter) => `v2/admin/content/promos/${filter.promo_id}/banners`,
    pathFilterField: 'promo_id',
  },
  // The "pick a banner to add" source list for the section above. Scoped to
  // `?template_type=home_page` (confirmed via `oaq.py`: `GET
  // content/banners` takes a real `template_type` filter) — the ticket's own
  // note is that these promo banners replace the regular home-page banners
  // "configured using home_page template type and tags for home page", so
  // the picker only offers banners actually built for that slot, same
  // `content/banners` endpoint `category-banner-options` already confirms
  // works, just scoped rather than bare.
  'promo-banner-options': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'banner_id',
    fetchAllForGetMany: true,
    listPath: 'content/banners?template_type=home_page',
  },
  // Batch sends (Blasts tab, HATCH-905, `GET /admin/batch-sends?blog_id=`,
  // confirmed via `oaq.py`) — real base path is `admin/batch-sends`, not
  // under `v2/`, same shape as every other `RESOURCE_CONFIG` entry here:
  // one real path per resource, not a guessed convention. Real primary key
  // is `batch_send_id`. `?before=&after=` are real date-time cursor params
  // (`created_at`-ordered, confirmed via `oaq.py`) — the project-wide
  // `DEFAULT_RESOURCE_CONFIG` cursor defaults (`cursorField: 'created_at'`,
  // `cursorParam: 'after'`) already match, so no override needed. `blog_id`
  // is also a real query param with a matching name, so no `filterParamMap`
  // entry either — `BlogBlastsTable`'s own `filter={{ blog_id }}` (apps/blog)
  // passes straight through.
  'batch-sends': {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'batch_send_id',
    listPath: 'admin/batch-sends',
  },
  // `apps/flows` (HATCH-575) is a `bot-api` app — no RESOURCE_CONFIG entry
  // here; it talks to `botHttpClient` directly, same as `view-single-session`/
  // `session-list`/`schemas` (see `resource-map.yaml`'s `bot-api` entry:
  // this file's single `httpClient` stays wired to `marketplace-rest-api`
  // only, `bot-api` has its own base URL/auth `dataProvider` can't reach).

  // Subscribers (apps/subscribers, this ticket) — real base path is
  // `v2/admin/subscribers` (confirmed via `oaq.py`), not the bare resource
  // name. `subscriber_id` is the real primary key; `subscribed_at` is the
  // real monotonically-increasing timestamp field (the source export's own
  // `_columnKey` maps its cursor column to `subscribed_at`, not
  // `created_at`) — `?after=&before=&limit=&email=` on the live `GET`
  // endpoint already match this project's cursor-pagination default and
  // its own filter param name, so no `cursorParam`/`filterParamMap`
  // override is needed beyond `cursorField`. No `includeIdInPatchBody`:
  // `UpdateSubscriber`'s body has no `subscriber_id` field at all.
  subscribers: {
    ...DEFAULT_RESOURCE_CONFIG,
    idField: 'subscriber_id',
    cursorField: 'subscribed_at',
    listPath: 'v2/admin/subscribers',
    getOnePath: (id) => `/v2/admin/subscribers/${id}`,
    updatePath: (id) => `/v2/admin/subscribers/${id}`,
  },
}

export const dataProvider: DataProvider = createDataProvider(RESOURCE_CONFIG)
